import {RootState} from 'app/rootReducer';
import {SliceStatus} from 'interfaces';

import {createSlice} from '@reduxjs/toolkit';

import {
  getTikTokAuthUrl,
  getTikTokAuthUrlFailure,
  getTikTokAuthUrlSuccess,
  syncTikTokAccount,
  syncTikTokAccountFailure,
  syncTikTokAccountSuccess,
  uploadToTikTok,
  uploadToTikTokFailure,
  uploadToTikTokSuccess,
} from './tiktokActions';

interface TikTokState {
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  isUploading: boolean;
  error: string | null;
  status: SliceStatus;
}

export const tiktokSliceInitialState: TikTokState = {
  isAuthenticated: false,
  isAuthenticating: false,
  isUploading: false,
  error: null,
  status: SliceStatus.idle,
};

const tiktokSlice = createSlice({
  name: 'tiktok',
  initialState: tiktokSliceInitialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getTikTokAuthUrl, state => {
        state.isAuthenticating = true;
        state.status = SliceStatus.pending;
        state.error = null;
      })
      .addCase(getTikTokAuthUrlSuccess, state => {
        state.status = SliceStatus.resolved;
      })
      .addCase(getTikTokAuthUrlFailure, (state, action) => {
        state.isAuthenticating = false;
        state.status = SliceStatus.rejected;
        state.error = action.payload;
      })
      .addCase(syncTikTokAccount, state => {
        state.isAuthenticating = true;
        state.status = SliceStatus.pending;
        state.error = null;
      })
      .addCase(syncTikTokAccountSuccess, state => {
        state.isAuthenticated = true;
        state.isAuthenticating = false;
        state.status = SliceStatus.resolved;
      })
      .addCase(syncTikTokAccountFailure, (state, action) => {
        state.isAuthenticating = false;
        state.status = SliceStatus.rejected;
        state.error = action.payload;
      })
      .addCase(uploadToTikTok, state => {
        state.isUploading = true;
        state.status = SliceStatus.pending;
        state.error = null;
      })
      .addCase(uploadToTikTokSuccess, state => {
        state.isUploading = false;
        state.status = SliceStatus.resolved;
      })
      .addCase(uploadToTikTokFailure, (state, action) => {
        state.isUploading = false;
        state.status = SliceStatus.rejected;
        state.error = action.payload;
      });
  },
});

export const selectIsTikTokAuthenticated = (state: RootState) => {
  if (state.tiktok.isAuthenticated) {
    return true;
  }

  const userProfile = state.user.current;
  return !!(
    userProfile &&
    'tiktokData' in userProfile &&
    userProfile.tiktokData?.openId
  );
};

export const selectIsTikTokAuthenticating = (state: RootState) =>
  state.tiktok.isAuthenticating;

export const selectIsTikTokUploading = (state: RootState) =>
  state.tiktok.isUploading;

export const {reducer: tiktokReducer, name: tiktokReducerName} = tiktokSlice;
